/**
 * Marketing event layer.
 *
 * The app pushes semantic events onto the GTM dataLayer and GTM fans them out
 * to GA4 and the Meta Pixel, so adding or retiring a destination is a GTM
 * change rather than a deploy.
 *
 * Microsoft Clarity is the one exception: it is loaded directly in the root
 * layout (GTM is lazyOnload, which would cost it the start of every session),
 * so the same events are mirrored onto its SDK from here.
 *
 * Never push personally identifiable data (name, email, phone, message body)
 * onto the dataLayer: it would be forwarded to Meta unhashed, which breaks both
 * Meta's business tools terms and our privacy policy.
 */

// Opt-out rather than opt-in, so the existing GA4 setup keeps working with no
// env change. Set NEXT_PUBLIC_ENABLE_ANALYTICS=false on the dev deployment to
// keep its traffic out of the production audiences.
export const ANALYTICS_ENABLED =
  (process.env.NEXT_PUBLIC_ENABLE_ANALYTICS || "").toLowerCase() !== "false";

// Event names are the contract with GTM. Changing one here means updating the
// matching Custom Event trigger in the container.
export const ANALYTICS_EVENTS = {
  ROUTE_CHANGE: "route_change",
  CONTACT_FORM_SUBMIT: "contact_form_submit",
  SCHEDULE_CLICK: "schedule_click",
  CONTENT_VIEW: "content_view",
  NEWSLETTER_SIGNUP: "newsletter_signup",
  AI_NEXUS_CLICK: "ai_nexus_click",
  BOOKING_CONFIRMED: "booking_confirmed",
  CHAT_OPEN: "chat_open",
};

/**
 * Pushes an event onto the GTM dataLayer.
 *
 * Safe to call before the GTM container has loaded (it is lazyOnload): the
 * queue is created here and GTM replays whatever it finds on init.
 *
 * @param {string} event - Event name, from ANALYTICS_EVENTS.
 * @param {Object} [payload] - Extra, non-identifying parameters.
 */
export function pushEvent(event, payload = {}) {
  if (!ANALYTICS_ENABLED || typeof window === "undefined") return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...payload });

  forwardToClarity(event);
}

/**
 * Mirrors the event name onto Clarity so a session recording can be filtered by
 * what the visitor actually did ("show me sessions that submitted the contact
 * form"). Clarity is the one destination not behind GTM, so the fan-out happens
 * here instead of in the container.
 *
 * Only the event name is forwarded. The payload may narrow a session down to a
 * single visitor, and Clarity replays it next to the recording.
 *
 * @param {string} event - Event name, from ANALYTICS_EVENTS.
 */
function forwardToClarity(event) {
  // Clarity already segments recordings by URL, and a soft navigation on a
  // marketing site is not an intent signal worth a custom event.
  if (event === ANALYTICS_EVENTS.ROUTE_CHANGE) return;

  // Calls made before the tag finishes loading are queued by its stub, so the
  // only case to guard is Clarity not being configured at all.
  if (typeof window.clarity !== "function") return;

  window.clarity("event", event);
}

/**
 * Attaches a filterable key/value to the current Clarity session.
 *
 * @param {string} key - Tag name, shown in the Clarity recordings filter.
 * @param {string|string[]} value - Non-identifying tag value.
 */
export function tagClaritySession(key, value) {
  if (!ANALYTICS_ENABLED || typeof window === "undefined") return;
  if (typeof window.clarity !== "function") return;

  window.clarity("set", key, value);
}

/** Client-side navigation. GTM maps this to GA4 page_view and Meta PageView. */
export function trackRouteChange({ pagePath, pageTitle }) {
  pushEvent(ANALYTICS_EVENTS.ROUTE_CHANGE, {
    page_path: pagePath,
    page_title: pageTitle,
  });
}

/** Contact form accepted by the API. GTM maps this to Meta Lead. */
export function trackContactFormSubmit({ subject }) {
  pushEvent(ANALYTICS_EVENTS.CONTACT_FORM_SUBMIT, { form_subject: subject });
}

/** Click on any consultation CTA. GTM maps this to Meta Schedule. */
export function trackScheduleClick({ linkText }) {
  pushEvent(ANALYTICS_EVENTS.SCHEDULE_CLICK, { link_text: linkText });
}

/**
 * A section was on screen long enough to have been read. GTM maps this to Meta
 * ViewContent, so content_name is what separates one section from another in
 * reporting — always pass a distinct one.
 */
export function trackContentView({ contentName }) {
  pushEvent(ANALYTICS_EVENTS.CONTENT_VIEW, { content_name: contentName });
}

/**
 * Newsletter signup accepted. GTM maps this to Meta CompleteRegistration rather
 * than Lead: an email address is far weaker intent than a contact form, and
 * mixing the two would push Meta to optimise for the cheaper action.
 */
export function trackNewsletterSignup({ sourcePage }) {
  pushEvent(ANALYTICS_EVENTS.NEWSLETTER_SIGNUP, { source_page: sourcePage });
}

/**
 * Click through to the AI Nexus site. That site carries no tracking of its own,
 * so this is the last point at which the visitor is still visible to us.
 */
export function trackAiNexusClick({ linkText, sourcePage }) {
  pushEvent(ANALYTICS_EVENTS.AI_NEXUS_CLICK, {
    link_text: linkText,
    source_page: sourcePage,
  });
}

/**
 * A Cal.com booking actually completed. GTM maps this to Meta Schedule, which
 * is reserved for real bookings rather than clicks through to the booking page.
 */
export function trackBookingConfirmed({ eventTypeId }) {
  pushEvent(ANALYTICS_EVENTS.BOOKING_CONFIRMED, { event_type_id: eventTypeId });
}

/** Live chat widget opened. GTM maps this to Meta Contact. */
export function trackChatOpen() {
  pushEvent(ANALYTICS_EVENTS.CHAT_OPEN);
}
